import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { supabase } from '../../lib/supabase';

const FALLBACK_CONTENT = [
{ id: 1, title: 'Neon Dungeon Crawler', type: 'game', creator: 'PixelWizard', creatorId: 'pixelwizard', plays: 12400, likes: 890, remixes: 23, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_162820546-1773033728918.png", thumbnailAlt: 'Neon-lit cyberpunk dungeon with glowing corridors', trending: true, badge: '🔥 Hot', section: 'trending' },
{ id: 2, title: 'Dragon Flight Animation', type: 'animation', creator: 'AnimaStudio', creatorId: 'animastudio', plays: 8700, likes: 654, remixes: 15, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_13f179f9f-1773039607602.png", thumbnailAlt: 'Majestic dragon soaring through sunset clouds', trending: true, badge: '⭐ Featured', section: 'staff-picks' },
{ id: 3, title: 'Neon City Beats', type: 'music-video', creator: 'BeatMaker99', creatorId: 'beatmaker99', plays: 21000, likes: 1820, remixes: 34, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1c991270d-1773039609949.png", thumbnailAlt: 'Vibrant neon city with pixel art characters dancing', trending: true, badge: '🎵 Viral', section: 'trending' },
{ id: 4, title: 'Space Odyssey Intro', type: 'video', creator: 'CinematicAI', creatorId: 'cinematicai', plays: 5400, likes: 432, remixes: 9, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1732f8001-1773074694734.png", thumbnailAlt: 'Silver spaceship landing on alien planet', trending: false, badge: null, section: 'newest' },
{ id: 5, title: 'Crystal Collector', type: 'game', creator: 'GameForger', creatorId: 'gameforger', plays: 9800, likes: 720, remixes: 18, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_172e3dc54-1773039608615.png", thumbnailAlt: 'Colorful platformer with glowing crystals', trending: false, badge: '🏆 Top Rated', section: 'staff-picks' },
{ id: 6, title: 'Memory Match Animals', type: 'game', creator: 'KidsCraft', creatorId: 'kidscraft', plays: 6200, likes: 510, remixes: 12, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_118f89cbe-1764674284538.png", thumbnailAlt: 'Colorful memory card game with cartoon animals', trending: false, badge: null, section: 'newest' },
{ id: 7, title: 'Pixel Racer Turbo', type: 'game', creator: 'SpeedDev', creatorId: 'speeddev', plays: 15600, likes: 1240, remixes: 28, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_174fa0eb5-1773033727798.png", thumbnailAlt: 'Retro pixel art racing track with neon speed lines', trending: true, badge: '🚀 Rising', section: 'trending' },
{ id: 8, title: 'Cosmic Shooter', type: 'game', creator: 'StarForge', creatorId: 'starforge', plays: 7300, likes: 580, remixes: 11, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_14fcb1ba9-1772243734629.png", thumbnailAlt: 'Colorful space shooter with vibrant nebula', trending: false, badge: null, section: 'newest' },
{ id: 9, title: 'Afrobeat Groove', type: 'music', creator: 'BeatMaker99', creatorId: 'beatmaker99', plays: 9100, likes: 780, remixes: 41, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10f572d7e-1773074696218.png", thumbnailAlt: 'Colorful music visualization with African rhythm patterns', trending: true, badge: '🎶 Most Remixed', section: 'most-remixed' },
{ id: 10, title: 'Forest Guardian', type: 'game', creator: 'NatureGames', creatorId: 'naturegames', plays: 14200, likes: 2100, remixes: 52, thumbnail: "https://images.unsplash.com/photo-1731685131186-191e77db24e5", thumbnailAlt: 'Lush green forest with ancient trees and dappled sunlight', trending: false, badge: '🌿 Staff Pick', section: 'staff-picks' },
{ id: 11, title: 'Talking Avatar Demo', type: 'video', creator: 'AvatarAI', creatorId: 'avatarai', plays: 4500, likes: 380, remixes: 7, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7b9142a-1773074689373.png", thumbnailAlt: 'Animated talking character with realistic lip sync', trending: false, badge: null, section: 'newest' },
{ id: 12, title: 'Epic Orchestral Score', type: 'music', creator: 'SoundForge', creatorId: 'soundforge', plays: 6200, likes: 490, remixes: 19, thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a477ceab-1773074690790.png", thumbnailAlt: 'Dramatic orchestral music visualization', trending: false, badge: null, section: 'newest' }];

const FALLBACK_CREATORS = [
{ id: 1, name: 'PixelWizard', username: 'pixelwizard', avatar: 'PW', level: 'Master Creator', creations: 24, followers: 1840, color: '#6366F1', verified: true },
{ id: 2, name: 'AnimaStudio', username: 'animastudio', avatar: 'AS', level: 'Innovator', creations: 18, followers: 1230, color: '#F43F5E', verified: true },
{ id: 3, name: 'BeatMaker99', username: 'beatmaker99', avatar: 'BM', level: 'Builder', creations: 12, followers: 890, color: '#10B981', verified: false },
{ id: 4, name: 'CinematicAI', username: 'cinematicai', avatar: 'CA', level: 'Creator', creations: 8, followers: 540, color: '#F59E0B', verified: false }];

const SECTIONS = [
{ id: 'all', label: 'All', icon: 'Grid3X3' },
{ id: 'trending', label: 'Trending', icon: 'TrendingUp' },
{ id: 'newest', label: 'Newest', icon: 'Clock' },
{ id: 'staff-picks', label: 'Staff Picks', icon: 'Star' },
{ id: 'most-remixed', label: 'Most Remixed', icon: 'Shuffle' }];

const TYPE_TABS = [
{ id: 'all', label: 'All', icon: 'Grid3X3' },
{ id: 'game', label: 'Games', icon: 'Gamepad2' },
{ id: 'animation', label: 'Animations', icon: 'Film' },
{ id: 'video', label: 'Videos', icon: 'Video' },
{ id: 'music-video', label: 'Music Video', icon: 'Music' },
{ id: 'music', label: 'Music', icon: 'Music2' }];

const TYPE_COLORS = {
  game: 'var(--color-primary)',
  animation: 'var(--color-accent)',
  video: '#10B981',
  'music-video': '#F43F5E',
  music: '#8B5CF6'
};

const TYPE_LABELS = {
  game: 'Game',
  animation: 'Animation',
  video: 'Video',
  'music-video': 'Music Video',
  music: 'Music'
};

function ContentCard({ item, onPlay, onRemix }) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(item?.likes);
  const typeColor = TYPE_COLORS?.[item?.type] || 'var(--color-primary)';
  const typeLabel = TYPE_LABELS?.[item?.type] || 'Creation';

  return (
    <div
      className="rounded-2xl overflow-hidden group transition-all duration-200 hover:shadow-lg cursor-pointer"
      style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}
      onClick={() => onPlay?.(item)}>
      
      <div className="relative h-40 overflow-hidden">
        <Image src={item?.thumbnail} alt={item?.thumbnailAlt || item?.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: typeColor, color: 'white', fontFamily: 'var(--font-caption)' }}>
          {typeLabel}
        </span>
        {item?.badge &&
        <span className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(0,0,0,0.7)', color: 'white', fontFamily: 'var(--font-caption)', backdropFilter: 'blur(4px)' }}>
            {item?.badge}
          </span>
        }
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
            <Icon name="Play" size={20} color="#1a1a1a" />
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.title}</h3>
        <p className="text-xs mb-2" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>by {item?.creator}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
              <Icon name="Play" size={11} />
              <span style={{ fontFamily: 'var(--font-data)' }}>{item?.plays?.toLocaleString()}</span>
            </div>
            <button
              onClick={(e) => {e?.stopPropagation();setLiked((v) => !v);setLikes((v) => liked ? v - 1 : v + 1);}}
              className="flex items-center gap-1 text-xs transition-all hover:scale-110"
              style={{ color: liked ? '#F43F5E' : 'var(--color-muted-foreground)' }}>
              
              <Icon name="Heart" size={11} color={liked ? '#F43F5E' : 'var(--color-muted-foreground)'} fill={liked ? '#F43F5E' : 'none'} />
              <span style={{ fontFamily: 'var(--font-data)' }}>{likes?.toLocaleString()}</span>
            </button>
          </div>
          <button
            onClick={(e) => {e?.stopPropagation();onRemix?.(item);}}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-all hover:opacity-80"
            style={{ borderColor: `${typeColor}40`, background: `${typeColor}08`, color: typeColor, fontFamily: 'var(--font-caption)' }}>
            
            <Icon name="Shuffle" size={10} color={typeColor} />
            Remix ({item?.remixes})
          </button>
        </div>
      </div>
    </div>);

}

function CreatorCard({ creator, onClick }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5"
      style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
      onClick={() => onClick?.(creator)}>
      
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${creator?.color}, ${creator?.color}aa)`, fontFamily: 'var(--font-heading)' }}>
        
        {creator?.avatar}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <p className="text-sm font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{creator?.name}</p>
          {creator?.verified && <Icon name="BadgeCheck" size={13} color={creator?.color} />}
        </div>
        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{creator?.level}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{creator?.followers?.toLocaleString()}</p>
        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>followers</p>
      </div>
    </div>);

}

export default function DiscoverPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('all');
  const [activeType, setActiveType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [content, setContent] = useState(FALLBACK_CONTENT);
  const [creators, setCreators] = useState(FALLBACK_CREATORS);
  const [loadingCreators, setLoadingCreators] = useState(false);

  const loadCreators = useCallback(async () => {
    setLoadingCreators(true);
    try {
      const { data } = await supabase?.from('creators')?.select('*')?.order('followers_count', { ascending: false })?.limit(8);
      if (data?.length > 0) {
        setCreators(data?.map((c) => ({
          id: c?.id,
          name: c?.display_name,
          username: c?.username,
          avatar: c?.display_name?.slice(0, 2)?.toUpperCase(),
          level: c?.total_creations > 20 ? 'Master Creator' : c?.total_creations > 10 ? 'Innovator' : 'Creator',
          creations: c?.total_creations || 0,
          followers: c?.followers_count || 0,
          color: '#6366F1',
          verified: c?.total_creations > 5
        })));
      }
    } catch (err) {

      // keep fallback
    } finally {setLoadingCreators(false);
    }
  }, []);

  useEffect(() => {loadCreators();}, [loadCreators]);

  const filtered = useMemo(() => {
    let list = [...content];
    if (activeSection !== 'all') list = list?.filter((c) => c?.section === activeSection);
    if (activeType !== 'all') list = list?.filter((c) => c?.type === activeType);
    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter((c) => c?.title?.toLowerCase()?.includes(q) || c?.creator?.toLowerCase()?.includes(q));
    }
    return list;
  }, [content, activeSection, activeType, searchQuery]);

  const trending = content?.filter((c) => c?.trending)?.slice(0, 3);

  const handlePlay = (item) => {
    if (item?.type === 'game') navigate('/game-player-screen', { state: { game: item } });else
    if (item?.type === 'video') navigate(`/video/${item?.id}`, { state: { item } });else
    if (item?.type === 'animation') navigate(`/animation/${item?.id}`, { state: { item } });else
    if (item?.type === 'music-video') navigate(`/music-video/${item?.id}`, { state: { item } });else
    navigate('/creator-dashboard');
  };

  const handleRemix = (item) => navigate('/creator-dashboard', { state: { remixItem: item } });
  const handleCreatorClick = (creator) => navigate(`/creator/${creator?.username}`);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Discover</h1>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Trending games, videos, animations, and music from the community</p>
            </div>
            <button
              onClick={() => navigate('/creator-dashboard')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}>
              
              <Icon name="Wand2" size={15} color="white" />
              Create
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search creations, creators..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm border outline-none transition-all"
              style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }} />
            
          </div>

          {/* Section filter */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-6" style={{ scrollbarWidth: 'none' }}>
            {SECTIONS?.map((section) =>
            <button
              key={section?.id}
              onClick={() => setActiveSection(section?.id)}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border transition-all font-semibold"
              style={{
                borderColor: activeSection === section?.id ? 'var(--color-primary)' : 'var(--color-border)',
                background: activeSection === section?.id ? 'rgba(99,102,241,0.1)' : 'var(--color-card)',
                color: activeSection === section?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                fontFamily: 'var(--font-caption)'
              }}>
              
                <Icon name={section?.icon} size={13} color={activeSection === section?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                {section?.label}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main content */}
            <div className="lg:col-span-3">
              {/* Trending spotlight */}
              {activeSection === 'all' &&
              <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="TrendingUp" size={18} color="#F43F5E" />
                    <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Trending Now</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {trending?.map((item, idx) =>
                  <div
                    key={item?.id}
                    className={`relative rounded-2xl overflow-hidden cursor-pointer group ${idx === 0 ? 'md:col-span-2' : ''}`}
                    style={{ height: idx === 0 ? 220 : 160 }}
                    onClick={() => handlePlay(item)}>
                    
                        <Image src={item?.thumbnail} alt={item?.thumbnailAlt || item?.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%)' }} />
                        <div className="absolute bottom-3 left-3 right-3">
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: TYPE_COLORS?.[item?.type] || 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-caption)' }}>
                            {item?.badge || TYPE_LABELS?.[item?.type]}
                          </span>
                          <h3 className="text-white font-bold mt-1" style={{ fontFamily: 'var(--font-heading)', fontSize: idx === 0 ? 18 : 14 }}>{item?.title}</h3>
                          <p className="text-white/70 text-xs mt-0.5" style={{ fontFamily: 'var(--font-caption)' }}>by {item?.creator} · {item?.plays?.toLocaleString()} plays</p>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                            <Icon name="Play" size={20} color="#1a1a1a" />
                          </div>
                        </div>
                      </div>
                  )}
                  </div>
                </section>
              }

              {/* Type filter */}
              <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4" style={{ scrollbarWidth: 'none' }}>
                {TYPE_TABS?.map((tab) =>
                <button
                  key={tab?.id}
                  onClick={() => setActiveType(tab?.id)}
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all font-semibold"
                  style={{
                    borderColor: activeType === tab?.id ? 'var(--color-primary)' : 'var(--color-border)',
                    background: activeType === tab?.id ? 'rgba(99,102,241,0.1)' : 'transparent',
                    color: activeType === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                    fontFamily: 'var(--font-caption)'
                  }}>
                  
                    <Icon name={tab?.icon} size={12} color={activeType === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                    {tab?.label}
                  </button>
                )}
              </div>

              {/* Content grid */}
              {filtered?.length === 0 ?
              <div className="flex flex-col items-center justify-center py-16 gap-3" style={{ border: '1px dashed var(--color-border)', borderRadius: 16 }}>
                  <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                  <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>No results found</p>
                </div> :

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {filtered?.map((item) =>
                <ContentCard key={item?.id} item={item} onPlay={handlePlay} onRemix={handleRemix} />
                )}
                </div>
              }
            </div>

            {/* Sidebar: Popular Creators */}
            <div className="lg:col-span-1">
              <div className="sticky top-20">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Users" size={16} color="var(--color-primary)" />
                  <h2 className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Popular Creators</h2>
                </div>
                <div className="flex flex-col gap-2">
                  {creators?.slice(0, 6)?.map((creator) =>
                  <CreatorCard key={creator?.id} creator={creator} onClick={handleCreatorClick} />
                  )}
                </div>
                <button
                  onClick={() => navigate('/creator-dashboard')}
                  className="w-full mt-4 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-90"
                  style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}>
                  
                  Start Creating
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

}